import { area, intersect, polygon } from "@turf/turf";
import { Polygon, MultiPolygon, Position } from "geojson";
import { Layer, geoJSON, GeoJSON, LatLng } from "leaflet";
import { MeasureConfLayer, MeasureConf } from "types/types";
import { getGeojsonFeature, isGeomanLayer } from "./GeomanUtils";
import { getCutPolygon, latLonToLonLat } from "./TurfHelperUtils";
import { isArray } from "lodash-es";

export function fitBasePolygon(layer: Layer, basePolygon: GeoJSON) {
  const ToBeCutPolygon = getGeojsonFeature(layer);
  const overLayingPolygon = getGeojsonFeature(basePolygon);
  const intersection = intersect(overLayingPolygon, ToBeCutPolygon);

  return intersection;
}

export function getFlandersStudyAreaIntersection(
  flandersPolygon: MultiPolygon,
  layer: Layer | GeoJSON
) {
  const geoJsonFeature = getGeojsonFeature(layer);
  const intersection = intersect(flandersPolygon, geoJsonFeature);
  return intersection;
}

export function cutUnderlyingPolygons(
  overLayer: Layer,
  measuresConfigs: MeasureConf[],
  minArea: number,
  measureConfigIdBeingEdited?: number
) {
  return measuresConfigs.map((measureConfig) => {
    const { layers, ...restMeasureConfig } = measureConfig;
    const updatedLayers = cutMeasureConfigLayers(
      overLayer,
      measureConfig,
      layers,
      minArea,
      measureConfigIdBeingEdited
    );
    return { ...restMeasureConfig, layers: updatedLayers };
  });
}

function cutMeasureConfigLayers(
  overLayer: Layer,
  measureConfig: MeasureConf,
  layers: MeasureConfLayer[],
  minArea: number,
  measureConfigIdBeingEdited?: number
) {
  return layers.reduce(
    (layerCollection: MeasureConfLayer[], layer: MeasureConfLayer) => {
      if (
        layer.id === measureConfigIdBeingEdited ||
        measureConfig.measure.geometryType === "POINT"
      ) {
        return [...layerCollection, layer];
      }

      const toBeCutPolygon = getGeojsonFeature(layer.geometryLayer);
      const overlayingPolygon = getGeojsonFeature(overLayer);

      const cutPolygon = getCutPolygon(overlayingPolygon, toBeCutPolygon);

      if (cutPolygon === null) {
        //Layer has been completly overlapped
        layer.geometryLayer.remove();
        return [...layerCollection];
      }

      if (!cutPolygon) {
        //Layer was not overlapped
        return [...layerCollection, layer];
      }

      const cutPolygonSize = area(cutPolygon);

      //If the cut polygon is smaller than the min area, remove it
      if (cutPolygonSize < minArea) {
        layer.geometryLayer.remove();
        return [...layerCollection];
      }

      if (cutPolygon.geometry.type === "MultiPolygon") {
        const splitConfigMeasures: MeasureConfLayer[] =
          cutPolygon.geometry.coordinates.reduce(
            (newLayers: MeasureConfLayer[], polygonCoordinates) => {
              const newPolygon: Polygon = {
                type: "Polygon",
                coordinates: polygonCoordinates,
              };
              //if the new polygon is smaller than the min area, don't add it
              if (area(newPolygon) < minArea) {
                return [...newLayers];
              }
              const newLayer = geoJSON(newPolygon, {
                style: {
                  color: measureConfig.measure.color,
                  fillColor: measureConfig.measure.color,
                },
              });
              return [
                ...newLayers,
                {
                  id: Date.now() + Math.random(),
                  geometryLayer: newLayer,
                },
              ];
            },
            []
          );
        //Remove old layer from map
        layer.geometryLayer.remove();
        return [...layerCollection, ...splitConfigMeasures];
      }

      if (isGeomanLayer(layer.geometryLayer)) {
        // @ts-ignore
        layer.geometryLayer.setLatLngs(
          // @ts-ignore
          latLonToLonLat(cutPolygon.geometry.coordinates)
        );
      } else {
        //@ts-ignore
        layer.geometryLayer.clearLayers();
        //@ts-ignore
        layer.geometryLayer.addData(cutPolygon.geometry);
      }

      return [...layerCollection, layer];
    },
    []
  );
}

export function convertLatLngsToPolygon(polyLatLngs: LatLng | LatLng[]) {
  if (isArray(polyLatLngs) && polyLatLngs.length > 2) {
    const convertedLatLngs = polyLatLngs.map((latlng) => {
      const position: Position = [latlng.lng, latlng.lat];
      return position;
    });
    const [firstLatlng] = convertedLatLngs;
    const completedPolygon = [...convertedLatLngs, firstLatlng];

    return polygon([completedPolygon]);
  }
}
